/**
 * Détection best-effort du nom du composant React qui a rendu un élément,
 * via les fibers que React attache aux nœuds DOM (présents en dev/staging).
 * Ne lance jamais : renvoie undefined si rien n'est identifiable.
 */

interface FiberLike {
  type: unknown;
  return: FiberLike | null;
}

function fiberOf(el: Element): FiberLike | null {
  for (const key of Object.keys(el)) {
    if (key.startsWith("__reactFiber$")) {
      return (el as unknown as Record<string, FiberLike | null>)[key] ?? null;
    }
  }
  return null;
}

function nameOfType(type: unknown): string | undefined {
  if (typeof type === "function") {
    const fn = type as { displayName?: string; name?: string };
    return fn.displayName || fn.name || undefined;
  }
  if (typeof type === "object" && type !== null) {
    // forwardRef, memo…
    const obj = type as { displayName?: string; render?: { name?: string }; type?: unknown };
    if (obj.displayName) return obj.displayName;
    if (obj.render?.name) return obj.render.name;
    if (obj.type) return nameOfType(obj.type);
  }
  return undefined;
}

const GENERIC = new Set(["Fragment", "Suspense", "Provider", "Consumer"]);

export function detectComponentName(el: Element): string | undefined {
  try {
    let node: Element | null = el;
    let fiber: FiberLike | null = null;
    // Le fiber est parfois attaché à un ancêtre proche.
    while (node && !fiber) {
      fiber = fiberOf(node);
      node = node.parentElement;
    }
    let hops = 0;
    while (fiber && hops < 30) {
      const name = nameOfType(fiber.type);
      if (name && !GENERIC.has(name) && /^[A-Z]/.test(name)) return name;
      fiber = fiber.return;
      hops++;
    }
  } catch {
    // best effort, jamais bloquant
  }
  return undefined;
}
